/**
 * A quick task: made as a root task in its repo on the board on screen
 * (`board.quickTask()`), and queued for Claude as well, to start from the tmux
 * view's queue with its Send. The repo switcher's "+" comes here.
 */
import { ref } from "vue";
import { api } from "./api.js";
import { taskPrompt } from "./taskPrompt.js";
import { toast } from "./toast.js";
import { useBoard } from "../stores/board.js";
import { planFields, type PlanMode, type QueuedClaude } from "@shared/tmux.js";

/** A repo whose quick task was asked for from outside the tmux view — the
 *  Android widget's repo row, through `__todosNative.quickTask` (lib/taskRoute.ts).
 *  The tmux view opens its "Queue a task in" dialog for it and clears this. */
export const quickTaskIn = ref<string | null>(null);

const message = (err: unknown): string => (err instanceof Error ? err.message : String(err));

/** The notes are what Claude should do; the prompt is then the admin's brief
 *  (`taskPrompt()`), as the Claude modal would start with, so the template's
 *  `<notes>` carries them. `urgency` is the task's priority, as picked in the
 *  quick add (none: the server's floor), and `plan` how the queued row plans (`PlanMode`). `queue` is the whole queue after, and `id` the queued row's,
 *  when the queueing worked; `made` is false when not even the task was. */
export async function queueQuickTask(
  title: string,
  notes: string,
  repo: string,
  worktree: string | null = null,
  urgency?: number,
  plan: PlanMode = "off",
): Promise<{ made: boolean; queue: QueuedClaude[] | null; id: string | null }> {
  try {
    const task = await useBoard().quickTask(title.trim(), repo, notes.trim(), urgency);
    try {
      const { id, queue } = await api.post<{ id: string; queue: QueuedClaude[] }>("/api/tmux/queue", {
        taskId: task.id,
        prompt: taskPrompt(task),
        worktree,
        model: "default",
        ...(plan !== "off" ? planFields(plan) : {}),
      });
      toast(`Added to ${repo} and queued`);
      return { made: true, queue, id };
    } catch (err) {
      // The task is made either way; only the queueing failed.
      toast(`Added to ${repo}, not queued: ${message(err)}`);
      return { made: true, queue: null, id: null };
    }
  } catch (err) {
    toast(message(err));
    return { made: false, queue: null, id: null };
  }
}
